import { useMutation } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import {
    LTIModuleForm,
    ILTIModuleFormProps,
} from 'training/components/forms/LTIModuleForm';
import { useSnackbar } from 'common/hooks/useSnackbar';
import { useApolloError } from 'common/hooks/useApolloError';
import { PageTitle } from 'common/components/PageTitle';
import { IModuleCreateDrawerProps } from 'training/types';
import { gotoTrainingPage } from 'training/utils/gotoRoutes';
import { dayHoursToSeconds } from 'common/utils/formatDate';
import { createModuleInCache } from 'training/utils/module';
import { CreateLtiModuleDocument } from 'graphql/types';
import { getUrl } from 'route/utils/getUrl';

export const LTIModuleCreateDrawer = ({
    trainingId,
    moduleGroupId,
}: IModuleCreateDrawerProps) => {
    const [translate] = useTranslation();
    const [displaySnackbar] = useSnackbar();
    const navigate = useNavigate();
    const { showApolloError } = useApolloError();

    const [createModule] = useMutation(CreateLtiModuleDocument);

    const handleSubmit: ILTIModuleFormProps['onSubmit'] = async (values) => {
        const { image, ltiUrl, offset, ...otherValues } = values;

        const moduleValues = {
            ...otherValues,
            // The protocol is removed by the field so we always want to add https:// here.
            ltiUrl: `https://${ltiUrl}`,
            imageId: image?.id,
            offset: {
                seconds: dayHoursToSeconds(offset?.days, offset?.hours),
                type: offset?.type,
            },
        };

        // When moduleGroupId is given we use that for creating the module inside the moduleGroup, otherwise create it
        // in the training which will make a new moduleGroup
        const parentId = moduleGroupId ? { moduleGroupId } : { trainingId };

        try {
            await createModule({
                variables: { module: moduleValues, ...parentId },
                update: (cache, { data }) => {
                    const { module: newModule, moduleGroup } =
                        data?.createLtiModule || {};

                    if (!newModule || !moduleGroup) return;

                    createModuleInCache(
                        newModule,
                        moduleGroup,
                        !moduleGroupId, // Is new module group
                        trainingId,
                        cache
                    );
                },
            });
        } catch (error) {
            showApolloError(error);

            return;
        }

        displaySnackbar(translate('moduleActionSuccess.lti.create'), {
            variant: 'success',
        });

        // Go back to training to close drawer
        gotoTrainingPage(navigate, trainingId);

        return;
    };

    return (
        <>
            <PageTitle>{translate('newLTIModule')}</PageTitle>

            <LTIModuleForm
                moduleGroupId={moduleGroupId}
                onClose={() => {
                    const routeName = moduleGroupId
                        ? 'TRAINING_CREATE_MODULE_IN_GROUP'
                        : 'TRAINING_CREATE_MODULE';

                    const url = getUrl(routeName, {
                        trainingId,
                        ...(moduleGroupId && { moduleGroupId }),
                    });

                    navigate(url);
                }}
                onSubmit={handleSubmit}
            />
        </>
    );
};
